/**
 * VisualReportPage — `/candle-card/report/:consultationId`
 * --------------------------------------------------------
 * A report shown as pictures of the client's own face, not as numbers.
 *
 * One measurement at a time. The photo fills the page, the points and lines
 * that produced the finding are drawn on it, and the client steps forward and
 * back through the set. Changing the measurement changes the drawing; changing
 * the face state changes the photo under it.
 *
 * Two rules keep the picture honest:
 *
 *   - stepping to a measurement switches the photo to a pose that ACTUALLY
 *     fed it, so the drawing always sits on evidence rather than on whichever
 *     face happened to be on screen;
 *   - nothing is drawn that the agent did not record. The geometry comes
 *     straight from the bot's measure map, so a bot that changes what it
 *     measures changes this page with no code here to update.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, IconButton, Skeleton, Stack, Typography,
} from '@mui/material';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Container from '@/components/PageSheet';
import FaceCanvas from '@/features/candleCard/FaceCanvas';
import MeasureLegend from '@/features/candleCard/MeasureLegend';
import PoseSwitcher from '@/features/candleCard/PoseSwitcher';
import { paintMeasure } from '@/features/candleCard/measurePaint';
import { paintFor } from '@/features/candleCard/reportAdapters';
import { consultationsApi } from '@/services/api';

export default function VisualReportPage() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === 'rtl';
  const lang = i18n.language?.startsWith('fa') ? 'fa' : 'en';

  const [step, setStep] = useState(0);
  const [pose, setPose] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['consultation-evidence', consultationId],
    queryFn: () => consultationsApi.getEvidence(consultationId),
  });

  const frames = useMemo(() => data?.frames || [], [data]);
  // A measure with nothing to draw would be a blank step the client has to
  // page past, so it is not a step at all.
  const measures = useMemo(
    () => (data?.measures || []).filter((m) => (m?.points?.length || m?.lines?.length)),
    [data],
  );

  const measure = measures[step] || null;
  const contributing = useMemo(() => {
    const named = (measure?.poses || []).filter((p) => frames.some((f) => f.pose === p));
    return named.length ? named : frames.map((f) => f.pose);
  }, [measure, frames]);

  // Stepping to a measurement puts the client on a pose that fed it — unless
  // the pose they are already on is one of them, in which case changing the
  // photo under them would be disorienting for no gain.
  useEffect(() => {
    if (!frames.length) return;
    setPose((current) => (
      current && contributing.includes(current) ? current : contributing[0] || frames[0].pose
    ));
  }, [contributing, frames]);

  const frame = frames.find((f) => f.pose === pose) || frames[0] || null;
  const fmt = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(n);

  // What colour each point and line should be. The bot's descriptor says what
  // its landmark numbers mean; the paint layer turns that plus the bot's own
  // figures into colours. Both degrade on their own, so an unknown bot still
  // draws — in one colour, which is all that would be true of it.
  const paint = useMemo(
    () => (measure ? paintMeasure(measure, data?.metrics, paintFor(data?.agentKey)) : null),
    [measure, data],
  );

  // The arrows point the way the language reads.
  const Prev = rtl ? ArrowForwardIosRoundedIcon : ArrowBackIosNewRoundedIcon;
  const Next = rtl ? ArrowBackIosNewRoundedIcon : ArrowForwardIosRoundedIcon;
  const go = (delta) => setStep((s) => Math.min(measures.length - 1, Math.max(0, s + delta)));

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Skeleton variant="rounded" sx={{ aspectRatio: '1 / 1', borderRadius: 3 }} />
        <Skeleton variant="rounded" height={86} sx={{ mt: 2, borderRadius: 3 }} />
      </Container>
    );
  }

  if (isError || !frames.length || !measures.length) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 3 }}>
          {/* Captures are kept only as long as the card covers them, so an
              older report legitimately has no photos left to draw on. Saying
              that is better than an empty frame. */}
          {t('candleCard.visual.unavailable')}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/candle-card')}>
          {t('candleCard.visual.backToCard')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 1.5, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={800} component="h1">
          {t('candleCard.visual.title')}
        </Typography>
        <IconButton onClick={() => navigate('/candle-card')}
          aria-label={t('candleCard.visual.backToCard')}>
          <CloseRoundedIcon />
        </IconButton>
      </Stack>

      <FaceCanvas frame={frame} measure={measure} paint={paint} />

      {/* What is being shown, between the picture and the controls that change
          it — so the name sits with the drawing it belongs to. */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.3 }}>
          {t(`candleCard.measure.${measure.key}`, { defaultValue: measure.label || measure.key })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: .25 }}>
          {t('candleCard.visual.stepOf', {
            n: fmt(step + 1), total: fmt(measures.length),
          })}
        </Typography>
      </Box>

      {/* Every colour on the photo, named — and for a graded measurement, the
          same scale the written report prints. */}
      <MeasureLegend paint={paint} t={t} lang={lang} />

      <Stack direction="row" alignItems="center" justifyContent="center"
        spacing={1.5} sx={{ mt: 1.5 }}>
        <IconButton
          onClick={() => go(-1)}
          disabled={step === 0}
          aria-label={t('candleCard.visual.previous')}
          sx={{ border: '1px solid', borderColor: 'divider', bgcolor: '#FFFFFF' }}
        >
          <Prev sx={{ fontSize: 17 }} />
        </IconButton>

        {/* One mark per measurement: how far through you are, and a way to
            jump, without a scrollbar's worth of chrome. */}
        <Stack direction="row" spacing={.75} alignItems="center">
          {measures.map((m, i) => (
            <Box
              key={m.key || i}
              component="button"
              type="button"
              onClick={() => setStep(i)}
              aria-label={t(`candleCard.measure.${m.key}`, { defaultValue: m.label || m.key })}
              aria-current={i === step}
              sx={{
                p: 0, border: 'none', cursor: 'pointer',
                width: i === step ? 22 : 8, height: 8, borderRadius: 4,
                bgcolor: i === step ? '#C0A786' : 'rgba(17,34,62,.22)',
                transition: 'width .2s ease, background-color .2s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                '&:focus-visible': { outline: '2px solid #11223E', outlineOffset: 2 },
              }}
            />
          ))}
        </Stack>

        <IconButton
          onClick={() => go(1)}
          disabled={step >= measures.length - 1}
          aria-label={t('candleCard.visual.next')}
          sx={{ border: '1px solid', borderColor: 'divider', bgcolor: '#FFFFFF' }}
        >
          <Next sx={{ fontSize: 17 }} />
        </IconButton>
      </Stack>

      <Box sx={{ mt: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          {t('candleCard.visual.poseHint')}
        </Typography>
        <PoseSwitcher
          frames={frames}
          value={frame?.pose}
          onChange={setPose}
          contributing={contributing}
          t={t}
        />
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mt: 2.5, borderRadius: 3 }}>
        {t('candleCard.visual.footnote')}
      </Alert>
    </Container>
  );
}
